import React from "react";
import {
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import ListHeader from "./ListHeader";
import {
  IMerchantsExport,
  TMerchantsExportStatus,
} from "../../requests/merchantsExportRequests";
import { downloadFromURL } from "../../utils/orders";

interface IMerchantsExportTableProps {
  exports: IMerchantsExport[];
  loading: boolean;
  onDelete: (_id: number) => void;
  showMarketplace?: boolean;
  totalMarketplacesCount?: number;
}

const BASE_COLUMNS = [
  { label: "Fecha de solicitud" },
  { label: "Registros" },
  { label: "Tamaño" },
  { label: "Estado" },
  { label: "Acciones" },
];

const MARKETPLACE_COLUMN = { label: "Marketplace" };

type TChipColor = "default" | "success" | "error";

interface IStatusDisplay {
  label: string;
  color: TChipColor;
}

const STATUS_DISPLAY: Record<TMerchantsExportStatus, IStatusDisplay> = {
  pending: { label: "Pendiente", color: "default" },
  processing: { label: "Procesando", color: "default" },
  processed: { label: "Listo", color: "success" },
  error: { label: "Error", color: "error" },
};

const formatDate = (isoString: string) =>
  new Date(isoString).toLocaleDateString("es-AR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });

const formatFileSize = (fileSizeKb: number | null): string => {
  if (!fileSizeKb) return "-";
  return fileSizeKb >= 1024
    ? `${(fileSizeKb / 1024).toFixed(1)} MB`
    : `${fileSizeKb.toFixed(0)} KB`;
};

const MerchantsExportTable = ({
  exports,
  loading,
  onDelete,
  showMarketplace = false,
  totalMarketplacesCount,
}: IMerchantsExportTableProps) => {
  if (loading) return <CircularProgress size={24} />;

  if (!exports.length) {
    return (
      <Typography variant="body2" color="text.secondary" mt={1}>
        No hay exportaciones anteriores.
      </Typography>
    );
  }

  const columns = showMarketplace
    ? [MARKETPLACE_COLUMN, ...BASE_COLUMNS]
    : BASE_COLUMNS;

  return (
    <TableContainer component={Paper} className="m-t-20 br-20 bg-light">
      <Table>
        <ListHeader columns={columns} />
        <TableBody>
          {exports.map((exportRecord) => {
            const {
              id,
              createdAt,
              rowCount,
              fileSizeKb,
              status,
              downloadUrl,
              errorMessage,
              marketplaceNames,
            } = exportRecord;
            const statusDisplay = STATUS_DISPLAY[status];
            const isProcessing =
              status === "pending" || status === "processing";

            return (
              <TableRow key={id}>
                {showMarketplace && (
                  <TableCell className="p-l-20">
                    {marketplaceNames && totalMarketplacesCount && marketplaceNames.length >= totalMarketplacesCount
                      ? "TODOS"
                      : (marketplaceNames?.join(", ") ?? "-")}
                  </TableCell>
                )}
                <TableCell className={showMarketplace ? "" : "p-l-20"}>
                  {formatDate(createdAt)}
                </TableCell>
                <TableCell>{rowCount ?? "-"}</TableCell>
                <TableCell>{formatFileSize(fileSizeKb)}</TableCell>
                <TableCell>
                  <Tooltip
                    arrow
                    title={
                      status === "error" && errorMessage ? errorMessage : ""
                    }
                    placement="top"
                  >
                    <Chip
                      label={statusDisplay.label}
                      color={statusDisplay.color}
                      size="small"
                      icon={
                        isProcessing ? (
                          <CircularProgress size={12} color="inherit" />
                        ) : undefined
                      }
                    />
                  </Tooltip>
                </TableCell>
                <TableCell>
                  {status === "processed" && downloadUrl && (
                    <Tooltip arrow title="Descargar" placement="top">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => downloadFromURL(downloadUrl)}
                      >
                        <span className="material-symbols-rounded">
                          download
                        </span>
                      </IconButton>
                    </Tooltip>
                  )}
                  <Tooltip arrow title="Eliminar" placement="top">
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => onDelete(id)}
                    >
                      <span className="material-symbols-rounded">delete</span>
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default MerchantsExportTable;
